import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCompanyInfo,
  updateCompanyInfo,
  updateOpenClawConfig,
  updateHermesConfig,
  updateN8nConfig,
  updateWhatsAppCloudConfig,
  updatePaymentMethods,
  listConfiguredSecrets,
  setSecret,
  unsetSecret,
} from '@/lib/api/settings';
import type { SecretKey } from '@/lib/api/settings';
import type {
  CompanyInfo,
  HermesConfig,
  N8nConfig,
  OpenClawConfig,
  PaymentMethod,
  WhatsAppCloudConfig,
} from '@/types/domain';

const KEYS = {
  all: ['settings'] as const,
  company: () => [...KEYS.all, 'company'] as const,
  secrets: () => [...KEYS.all, 'secrets'] as const,
};

export const useCompanyInfo = () =>
  useQuery({
    queryKey: KEYS.company(),
    queryFn: () => getCompanyInfo(),
    staleTime: 60 * 1000,
  });

export const useUpdateCompanyInfo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Omit<CompanyInfo, 'id' | 'createdAt' | 'updatedAt'>>) =>
      updateCompanyInfo(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

export const useUpdateOpenClawConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<OpenClawConfig>) => updateOpenClawConfig(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/** Update Hermes (Baileys-based) WhatsApp gateway config. */
export const useUpdateHermesConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<HermesConfig>) => updateHermesConfig(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/** Update WhatsApp Cloud API config — highest priority gateway. */
export const useUpdateWhatsAppCloudConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<WhatsAppCloudConfig>) =>
      updateWhatsAppCloudConfig(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/** Update n8n orchestrator config — takes priority over Hermes / OpenClaw. */
export const useUpdateN8nConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<N8nConfig>) => updateN8nConfig(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

export const useUpdatePaymentMethods = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (methods: PaymentMethod[]) => updatePaymentMethods(methods),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/**
 * Names of the credentials that are configured — never their values.
 * Owner-only: the underlying function refuses anyone else, and it returns an
 * empty list rather than throwing, so a cashier opening Settings sees the
 * forms without an error banner.
 */
export const useConfiguredSecrets = () =>
  useQuery({
    queryKey: KEYS.secrets(),
    queryFn: () => listConfiguredSecrets(),
    staleTime: 60 * 1000,
  });

export const useSetSecret = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: SecretKey; value: string }) => setSecret(key, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.secrets() }),
  });
};

export const useUnsetSecret = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: SecretKey) => unsetSecret(key),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.secrets() }),
  });
};
